<style scoped>

  #map {
    width: 100%;
    height: 100%;
    min-height: 300px;
  }

</style>

<template>

  <v-container style="position: relative;">

    <v-snackbar v-model="loading" :top="true" :timeout="0" absolute style="z-index: 9999;">
      <v-progress-circular class="" indeterminate size="30"></v-progress-circular>
      Loading Station: {{$route.params.id}}
    </v-snackbar>

    <v-alert v-if="!stationLatest" class="" outline color="grey" icon="error_outline" :value="true">
      No station loaded.
    </v-alert>

    <v-layout>

      <v-flex xs6>
        <div id="map"></div>
      </v-flex>

      <v-flex xs6 class="pl-1">

        <v-alert v-if="stationLatest.STATUS === 'ACTIVE'"
                 color="green" class="white--text mt-0" :value="true" style="text-transform: uppercase;">
          MesoWest Station Status:
          <v-chip small label color="green lighten-2" class="green--text text--darken-4">
            <v-icon left>thumb_up</v-icon>
            {{stationLatest.STATUS}}
          </v-chip>
          <br>
        </v-alert>

        <v-alert v-else
                 color="warning" class="black--text mt-0" :value="true" style="text-transform: uppercase;">
          MesoWest Station Status:
          <v-chip small label color="yellow" class="">
            <v-icon left>thumb_down</v-icon>
            {{stationLatest.STATUS}}
          </v-chip>
          <br>
        </v-alert>

        <h3 class="ma-0 brown--text">{{stationLatest.NAME}}</h3>

        <div>MesoWest Station ID: {{stationLatest.STID}}</div>
        <!--<div>MNET_LONGNAME: {{stationLatest.MNET_LONGNAME}}</div>-->
        <div>Elevation: {{stationLatest.ELEVATION}} ft.</div>

        <v-btn fab dark small class="right" @click="centerMap({zoom: 14})">
          <v-icon>center_focus_strong</v-icon>
        </v-btn>

      </v-flex>

    </v-layout>

    <v-divider class="mt-2"></v-divider>

    <h3 class="mt-2 pb-0 mb-0">Last Known Observations:</h3>

    <!--<pre>{{stationResponse}}</pre>-->

    <v-data-table v-if="tableData.length"
                  class=""
                  :headers="headers"
                  :items="tableData"
                  hide-actions
    >
      <template slot="items" slot-scope="props">
        <tr @click="$router.push(`/variable/${props.item.variable}`)" class="link">
          <td style="height: auto;">{{ props.item.date }}</td>
          <td style="height: auto;" class="py-1"><span>{{ props.item.variable | formatName }}</span></td>
          <td style="height: auto;">{{ props.item.value }}</td>
          <td style="height: auto;">{{ props.item.unit }}</td>
        </tr>
      </template>
    </v-data-table>

  </v-container>

</template>

<script>
  import 'leaflet/dist/leaflet.css';
  import { Map, TileLayer, CircleMarker, Control } from 'leaflet';
  import { transform } from 'lodash';
  import { DateTime } from 'luxon';
  import favVars from '@/models/favorite-variables';
  import getStationLatest from '@/api/getStationLatest';

  export default {

    name: 'StationOverview',

    data() {
      return {
        loading: false,
        map: null,
        fab: false,
        stationLatest: {},
        stationMarker: null,
        stationLatLng: null,
        headers: [],
        tableData: [],
        stationResponse: null,
      };
    },

    mounted() {
      this.createMap();
      this.getLatestStationObs();
    },

    watch: {

      $route: {
        deep: true,
        async handler() {
          this.getLatestStationObs();
        },
      },

    },

    computed: {

      units() {
        return this.stationResponse.UNITS;
      },

      keyMap() {
        return transform(this.stationLatest.SENSOR_VARIABLES, (result, value, key) => {
          // eslint-disable-next-line
          result[key] = Object.keys(value)[0];
          return result;
        }, {});
      },

    },

    methods: {

      async getLatestStationObs() {
        this.loading = true;
        const stid = this.$route.params.id;
        console.warn('Getting latest info for:', stid);
        this.stationResponse = await getStationLatest(stid);
        this.stationLatest = this.stationResponse.STATION[0];
        this.createStationMarker(this.stationLatest);
        this.createTableData(this.stationLatest);
        this.map.invalidateSize();
        this.loading = false;
      },

      createStationMarker(station) {
        // set station lat, lng
        this.stationLatLng = { lon: station.LONGITUDE, lat: station.LATITUDE };

        // validate station is created, and create if it doesn't exist
        // if it exists, just set the new lat, lng
        if (!this.stationMarker) {
          this.stationMarker = new CircleMarker(this.stationLatLng, {
            radius: 10,
          }).addTo(this.map);
        } else {
          this.stationMarker.setLatLng(this.stationLatLng);
        }

        // center the map on the new station lat, lng
        this.centerMap({
          latLng: this.stationLatLng,
          zoom: 13,
        });
      },

      createTableData(station) {
        this.tableData = [];
        this.headers = [
          { text: 'Date/Time', value: 'date', align: 'left', sortable: false },
          { text: 'Variable', value: 'variable', align: 'left', sortable: false },
          { text: 'Value', value: 'value', align: 'left', sortable: false },
          { text: 'Unit', value: 'unit', align: 'left', sortable: false },
        ];

        favVars.forEach((variable) => {
          const obs = station.OBSERVATIONS[this.keyMap[variable.name]];
          if (obs) {
            this.tableData.push({
              date: DateTime.fromISO(obs.date_time).toLocaleString(DateTime.DATETIME_SHORT),
              variable: variable.name,
              value: obs.value,
              unit: this.units[variable.name],
            });
          }
        });
      },

      createMap() {
        // Google layer key
        // h = roads only
        // m = standard roadmap
        // p = terrain
        // r = somehow altered roadmap
        // s = satellite only
        // t = terrain only
        // y = hybrid

        const baseLayers = {
          // eslint-disable-next-line
          'Google Satellite': new TileLayer('http://mt.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'),
          // eslint-disable-next-line
          'Google Terrain': new TileLayer('http://mt.google.com/vt/lyrs=p&x={x}&y={y}&z={z}'),
          'World Topo Map': new TileLayer('http://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}'),
          'World Street Map': new TileLayer('http://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}'),
          'Nat Geo World Map': new TileLayer('http://services.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}'),
          'Shaded Relief': new TileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}'),
        };

        const overlays = {
//          'Stamen Labels': new TileLayer('http://{s}.tile.stamen.com/terrain-labels/{z}/{x}/{y}.png'),
        };

        this.map = new Map('map', {
          center: [37.78000, -119.17806],
          zoom: 9,
          layers: [baseLayers['Google Terrain']],
          attributionControl: false,
        });

        new Control.Layers(baseLayers, overlays).addTo(this.map);
      },

      centerMap({ latlng = this.stationMarker.getLatLng(), zoom = 14 }) {
        this.map.setView(latlng, zoom);
      },

    },


    filters: {

      formatName(val) {
        return val.replace(/_/g, ' ').toUpperCase();
      },

    },

  };

</script>



// WEBPACK FOOTER //
// src/components/Station/Overview.vue